import React from 'react';
import { CircularProgress, Tooltip } from '@mui/material';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import LocalText from './LocalText';

const MESSAGES = {
  checking: 'Checking if URL is reachable...',
  valid:    'URL is valid and reachable',
  invalid:  'URL is not reachable or has an invalid format',
};

// Specific tooltip text for the 'invalid' status, keyed by the checker's
// failure reason (see useUrlValidation). Falls back to MESSAGES.invalid
// when no reason is available (e.g. generic 'url'/'hls'/'3d' fields).
const REASON_MESSAGES = {
  unreachable:      'URL could not be reached',
  'invalid-format': 'URL is reachable but is not a valid file',
};

const ICONS = {
  checking: <CircularProgress size={14} sx={{ color: '#5383b3' }} />,
  valid:    <CheckCircleOutlineIcon sx={{ fontSize: 16, color: '#2e7d32' }} />,
  invalid:  <ErrorOutlineIcon sx={{ fontSize: 16, color: '#d32f2f' }} />,
};

/**
 * Shows a status icon with a translated tooltip explaining the URL check result.
 * Returns null when status is 'idle' (empty field — no icon shown).
 *
 * Usage:
 *   <UrlStatusIcon status={urlStatus} />
 *   <UrlStatusIcon status={urlStatus} reason={urlReason} />
 */
const UrlStatusIcon = ({ status, reason }) => {
  if (!status || status === 'idle' || !ICONS[status]) return null;

  const message = status === 'invalid' && REASON_MESSAGES[reason]
    ? REASON_MESSAGES[reason]
    : MESSAGES[status];

  return (
    <Tooltip
      title={<LocalText>{message}</LocalText>}
      placement="top"
      arrow
    >
      <span style={{ display: 'flex', alignItems: 'center', cursor: 'help' }}>
        {ICONS[status]}
      </span>
    </Tooltip>
  );
};

export default UrlStatusIcon;
